import React, { useContext, useState, useEffect } from "react"
import _ from "lodash"
import { Link } from "react-router-dom"
import { useLazyQuery, useMutation, useQuery } from "@apollo/client"

import { InputSwitch } from "primereact/inputswitch"

import { LoaderMedium } from "../../components/Loaders"
import { ElementLoading } from "../../components/ElementLoading"
import GenericTable from "../../components/DataTable/GenericTable"
import FilterHeader from "../../components/DataTable/FilterHeader"
import { useFilters } from "../../components/Filters/useFilters"
import PageHeader from "../../components/PageHeader"
import PageSidebarContent from "../../components/sidebars/PageSidebarContent"
import EnterpriseMenuSidebar from "../../components/sidebars/EnterpriseMenuSidebar"
import { UserContext } from "../../context/userContext"
import {
  CHANGE_STATUS_LOCATION,
  GET_LOCATIONS_BY_ENTERPRISE,
  GET_ORGANIZATIONS,
} from "./queries"

export default function LocationsList() {
  const userCtx = useContext(UserContext)
  const [organizations, setOrganizations] = useState([])
  const [locations, setLocations] = useState([])
  const [loadingPage, setLoadingPage] = useState(true)
  const [changeStatusLocation] = useMutation(CHANGE_STATUS_LOCATION)

  const {
    filters,
    globalFilterValue,
    onGlobalFilterChange,
    onFilterChange,
    clearGlobalFilter,
    clearAllFilters,
  } = useFilters()

  const [getLocations] = useLazyQuery(GET_LOCATIONS_BY_ENTERPRISE, {
    variables: {
      staff_id: userCtx.session.user?.staff_id,
      enterprise_id: userCtx.session.user?.enterprise_id,
    },
    context: {
      headers: {},
    },
    skip: !userCtx.session.user.staff_id,
    fetchPolicy: "no-cache",
    onCompleted: async (data) => {
      setLocations(
        _.map(data.locations, (location) => {
          const orgs = location?.organizations
            ? location.organizations.split(",").map((org) => org.trim())
            : []
          return {
            ...location,
            organization_name: _.unescape(location.organization_name),
            organization_names: orgs,
            organizations_display: orgs.join(", "),
            enterprise_managed: location.location_id_enterprise_managed
              ? "Yes"
              : "",
            status: (
              <ElementLoading
                status={location.location_status}
                item={location.location_id}
                parentCall={(value) =>
                  handleToggleChange(value, location.location_id)
                }
                organization_id={location.organization_id}
              >
                <InputSwitch checked={location.location_status === "ACTIVE"} />
              </ElementLoading>
            ),
            edit: (
              <Link
                to={`/enterprise/form/location/1/${location.location_id}`}
                className="btn"
              >
                Edit <i className="pi pi-pencil"></i>
              </Link>
            ),
          }
        })
      )
      setLoadingPage(false)
    },
  })
  const { data: organizationsData } = useQuery(GET_ORGANIZATIONS, {
    variables: {
      staff_id: userCtx.session.user?.staff_id,
      organization_id: userCtx.session.user?.organization_id,
    },
    skip: !userCtx.session.user.staff_id,
    fetchPolicy: "network-only",
  })
  useEffect(() => {
    if (organizationsData) {
      const organizationsOptions = _.map(
        organizationsData.organizations,
        ({ organization_id, organization_name }) => ({
          label: organization_name,
          value: organization_name,
        })
      )
      setOrganizations([{ label: "All", value: "" }, ...organizationsOptions])
    }
  }, [organizationsData])

  const handleToggleChange = (value, location_id) => {
    return changeStatusLocation({
      variables: {
        staff_id: userCtx?.session.user.staff_id,
        staff_alias: userCtx?.session.user.staff_alias,
        location_id,
        location_status: value === true ? "ACTIVE" : "INACTIVE",
      },
      skip: !userCtx.session.user.staff_id,
      fetchPolicy: "no-cache",
      onCompleted: async () => {
        getLocations()
      },
    })
  }

  useEffect(() => {
    getLocations()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const filterConfig = [
    {
      label: "Organization",
      value: filters.organization_names?.value || "",
      options: organizations,
      onChange: (e) => onFilterChange("organization_names", e.value),
      onClear: () => onFilterChange("organization_names", null),
      placeholder: "Select an Organization",
      enableFilter: true,
    },
    {
      label: "Status",
      value: filters.location_status?.value || "",
      options: [
        { label: "All", value: "" },
        { label: "Active", value: "ACTIVE" },
        { label: "Inactive", value: "INACTIVE" },
      ],
      onChange: (e) => onFilterChange("location_status", e.value),
      onClear: () => onFilterChange("location_status", null),
      placeholder: "Select Status",
    },
  ]

  const columns = [
    { field: "location_name", header: "Location Name", sortable: true },
    {
      field: "enterprise_managed",
      header: "Enterprise Managed",
      sortable: true,
    },
    {
      field: "organizations_display",
      header: "Organization(s)",
      body: (rowData) => (
        <span>
          {rowData.organizations_display.split(", ").map((org, index) => (
            <span key={index}>
              {org.trim()}
              <br />
            </span>
          ))}
        </span>
      ),
    },
    { field: "status", header: "Status", sortable: true },
    { field: "edit", header: "Edit", sortable: true },
  ]

  const sidebarContent = <EnterpriseMenuSidebar />

  const setToLoading = loadingPage || userCtx.globalLoading

  if (setToLoading)
    return (
      <>
        <PageSidebarContent>{sidebarContent}</PageSidebarContent>
        <LoaderMedium />
      </>
    )

  return (
    <>
      <PageSidebarContent>{sidebarContent}</PageSidebarContent>
      <div className="main-content">
        <PageHeader
          title="Manage Locations"
          actions={[
            { to: "/enterprise/form/location/1/", label: "Create new Location" },
          ]}
        />
        <GenericTable
          data={locations}
          columns={columns}
          filters={filters}
          header={
            <FilterHeader
              filters={filterConfig}
              globalFilterValue={globalFilterValue}
              onGlobalFilterChange={onGlobalFilterChange}
              clearGlobalFilter={clearGlobalFilter}
              clearAllFilters={clearAllFilters}
            />
          }
          paginator
          rows={50}
          sortField="location_name"
          sortOrder={1}
        />
      </div>
    </>
  )
}
